// src/i18n.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import enSidebar from "./locales/en/sidebar.json";
import enDashboard from "./locales/en/dashboard.json";
import enForms from "./locales/en/forms.json";
import enTables from "./locales/en/tables.json";
import enValidation from "./locales/en/validation.json";
import enProfile from "./locales/en/profile.json";
import enSettings from "./locales/en/settings.json";
import enStudents from "./locales/en/students.json";
import enTeachers from "./locales/en/teachers.json";
import enParents from "./locales/en/parents.json";
import enSchoolAdmin from "./locales/en/schoolAdmin.json";
import enErrors from "./locales/en/errors.json";
import enNotifications from "./locales/en/notifications.json";
import enAuth from "./locales/en/auth.json";
import enHeader from "./locales/en/header.json";
import enLanding from "./locales/en/landing.json";
import enGamification from "./locales/en/gamification.json";
import enTutor from "./locales/en/tutor.json";
import enAssignments from "./locales/en/assignments.json";

import taCommon from "./locales/ta/common.json";
import taSidebar from "./locales/ta/sidebar.json";
import taDashboard from "./locales/ta/dashboard.json";
import taForms from "./locales/ta/forms.json";
import taTables from "./locales/ta/tables.json";
import taValidation from "./locales/ta/validation.json";
import taProfile from "./locales/ta/profile.json";
import taSettings from "./locales/ta/settings.json";
import taStudents from "./locales/ta/students.json";
import taTeachers from "./locales/ta/teachers.json";
import taParents from "./locales/ta/parents.json";
import taSchoolAdmin from "./locales/ta/schoolAdmin.json";
import taErrors from "./locales/ta/errors.json";
import taNotifications from "./locales/ta/notifications.json";
import taAuth from "./locales/ta/auth.json";
import taHeader from "./locales/ta/header.json";
import taLanding from "./locales/ta/landing.json";
import taGamification from "./locales/ta/gamification.json";
import taTutor from "./locales/ta/tutor.json";
import taAssignments from "./locales/ta/assignments.json";

import msCommon from "./locales/ms/common.json";
import msSidebar from "./locales/ms/sidebar.json";
import msDashboard from "./locales/ms/dashboard.json";
import msForms from "./locales/ms/forms.json";
import msTables from "./locales/ms/tables.json";
import msValidation from "./locales/ms/validation.json";
import msProfile from "./locales/ms/profile.json";
import msSettings from "./locales/ms/settings.json";
import msStudents from "./locales/ms/students.json";
import msTeachers from "./locales/ms/teachers.json";
import msParents from "./locales/ms/parents.json";
import msSchoolAdmin from "./locales/ms/schoolAdmin.json";
import msErrors from "./locales/ms/errors.json";
import msNotifications from "./locales/ms/notifications.json";
import msAuth from "./locales/ms/auth.json";
import msHeader from "./locales/ms/header.json";
import msLanding from "./locales/ms/landing.json";
import msGamification from "./locales/ms/gamification.json";
import msTutor from "./locales/ms/tutor.json";
import msAssignments from "./locales/ms/assignments.json";

export type AppLang = "en" | "ta" | "ms";

export const SUPPORTED_APP_LANGS: readonly AppLang[] = ["en", "ta", "ms"] as const;

export const STORAGE_KEY = "tkm_lang";

export const APP_NAMESPACES = [
  "common",
  "sidebar",
  "dashboard",
  "forms",
  "tables",
  "validation",
  "profile",
  "settings",
  "students",
  "teachers",
  "parents",
  "schoolAdmin",
  "errors",
  "notifications",
  "auth",
  "header",
  "landing",
  "gamification",
  "tutor",
  "assignments",
  "translation",
] as const;

type LocaleBundle = Record<string, unknown>;

function nestForLegacyTranslation(bundle: Record<string, LocaleBundle>): LocaleBundle {
  // Preserve existing t('sidebar.dev.dashboard') / t('common.x') call sites.
  return { ...bundle };
}

function buildLangResources(parts: Record<string, LocaleBundle>) {
  return {
    ...parts,
    translation: nestForLegacyTranslation(parts),
  };
}

const enParts: Record<string, LocaleBundle> = {
  common: enCommon,
  sidebar: enSidebar,
  dashboard: enDashboard,
  forms: enForms,
  tables: enTables,
  validation: enValidation,
  profile: enProfile,
  settings: enSettings,
  students: enStudents,
  teachers: enTeachers,
  parents: enParents,
  schoolAdmin: enSchoolAdmin,
  errors: enErrors,
  notifications: enNotifications,
  auth: enAuth,
  header: enHeader,
  landing: enLanding,
  gamification: enGamification,
  tutor: enTutor,
  assignments: enAssignments,
};

const taParts: Record<string, LocaleBundle> = {
  common: taCommon,
  sidebar: taSidebar,
  dashboard: taDashboard,
  forms: taForms,
  tables: taTables,
  validation: taValidation,
  profile: taProfile,
  settings: taSettings,
  students: taStudents,
  teachers: taTeachers,
  parents: taParents,
  schoolAdmin: taSchoolAdmin,
  errors: taErrors,
  notifications: taNotifications,
  auth: taAuth,
  header: taHeader,
  landing: taLanding,
  gamification: taGamification,
  tutor: taTutor,
  assignments: taAssignments,
};

const msParts: Record<string, LocaleBundle> = {
  common: msCommon,
  sidebar: msSidebar,
  dashboard: msDashboard,
  forms: msForms,
  tables: msTables,
  validation: msValidation,
  profile: msProfile,
  settings: msSettings,
  students: msStudents,
  teachers: msTeachers,
  parents: msParents,
  schoolAdmin: msSchoolAdmin,
  errors: msErrors,
  notifications: msNotifications,
  auth: msAuth,
  header: msHeader,
  landing: msLanding,
  gamification: msGamification,
  tutor: msTutor,
  assignments: msAssignments,
};

export const normalizeAppLang = (raw: string | null | undefined): AppLang => {
  if (!raw) return "en";
  const s = raw.toLowerCase().replace("_", "-");
  const primary = s.split("-", 1)[0];
  if (primary === "ta") return "ta";
  if (primary === "ms") return "ms";
  if (primary === "en") return "en";
  return "en";
};

export const readSavedLang = (): AppLang => {
  try {
    return normalizeAppLang(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "en";
  }
};

export const getCurrentLang = (): AppLang => normalizeAppLang(i18n.language || "en");

const initialLang = readSavedLang();

i18n.use(initReactI18next).init({
  resources: {
    en: buildLangResources(enParts),
    ta: buildLangResources(taParts),
    ms: buildLangResources(msParts),
  },
  lng: initialLang,
  fallbackLng: "en",
  defaultNS: "translation",
  ns: [...APP_NAMESPACES],
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  returnNull: false,
});

export const setAppLanguage = async (lng: AppLang) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // ignore
  }
  if (normalizeAppLang(i18n.language) !== lng) {
    await i18n.changeLanguage(lng);
  }
  document.documentElement.lang = lng === "ta" ? "ta" : lng === "ms" ? "ms" : "en";
};

export default i18n;
